import { create } from 'zustand';
import type { GanadoEnCorral } from '../../api-client/ganado/ganado';
import {
  cambiarZona,
  obtenerGanadoEnCorral,
  obtenerGanadoPorArete,
  ponerDatosBaja,
  ponerDatosParida,
  ponerDatosPreñada,
  quitarDatosBaja,
  quitarDatosParida,
  quitarDatosPreñada,
  type CambiarZonaRequest,
  type PonerDatosBajaRequest,
  type PonerDatosParidaRequest,
  type PonerDatosPreñadaRequest,
  type QuitarDatosBajaRequest,
  type QuitarDatosParidaRequest,
  type QuitarDatosPreñadaRequest,
  type TipoArete
} from '../../api-client/ganado/ganado.api';
import { useUIStore } from '../ui/ui.store';

export interface GanadoState {
  enCorral: GanadoEnCorral[];

  cargarPorCorral: (idCorral: number) => Promise<void>;
  cargarPorArete: (tipoArete: TipoArete, arete: string) => Promise<GanadoEnCorral | null>;
  ponerPreñada: (request: PonerDatosPreñadaRequest) => Promise<void>;
  ponerParida: (request: PonerDatosParidaRequest) => Promise<void>;
  darBaja: (request: PonerDatosBajaRequest) => Promise<void>;
  cambiarZona: (request: CambiarZonaRequest) => Promise<void>;
  quitarDatosPreñada: (request: QuitarDatosPreñadaRequest) => Promise<void>;
  quitarDatosParida: (request: QuitarDatosParidaRequest) => Promise<void>;
  quitarDatosBaja: (request: QuitarDatosBajaRequest) => Promise<void>;
}

const { showSuccessToast } = useUIStore.getState();

export const useGanadoStore = create<GanadoState>()((set) => ({
  enCorral: [],

  cargarPorCorral: async (idCorral: number) => {
    const data = await obtenerGanadoEnCorral(idCorral);

    if (data.isFailure) return;

    set({
      enCorral: data.result
    });
  },
  cargarPorArete: async (tipoArete, arete) => {
    const data = await obtenerGanadoPorArete(tipoArete, arete);

    if (data.isFailure) return null;

    set({
      enCorral: data.result ? [data.result] : []
    });

    return data.result;
  },
  ponerPreñada: async (request: PonerDatosPreñadaRequest) => {
    const response = await ponerDatosPreñada(request);

    if (response.isFailure) return;

    set((state) => ({
      enCorral: state.enCorral.map((animal) => {
        if (animal.folioInterno === request.folioInterno && animal.partida === request.partida) {
          return {
            ...animal,
            cargada: true,
            fechaTacto: request.fechaTacto,
            fechaParto: request.fechaParto,
            mesesGestacion: request.mesesGestacion,
            cargadaNotas: request.notas
          };
        }
        return animal;
      })
    }));

    showSuccessToast('Animal marcado como preñada');
  },
  ponerParida: async (request: PonerDatosParidaRequest) => {
    const response = await ponerDatosParida(request);

    if (response.isFailure) return;
    set((state) => ({
      enCorral: state.enCorral.map((animal) => {
        if (animal.folioInterno === request.folioInterno && animal.partida === request.partida) {
          return {
            ...animal,
            parida: true,
            paridaTipo: request.paridaTipo,
            paridaNotas: request.notas
          };
        }
        return animal;
      })
    }));

    showSuccessToast('Animal marcado como parida');
  },
  darBaja: async (request: PonerDatosBajaRequest) => {
    const response = await ponerDatosBaja(request);

    if (response.isFailure) return;

    set((state) => ({
      enCorral: state.enCorral.map((animal) => {
        if (animal.folioInterno === request.folioInterno && animal.partida === request.partida) {
          const status = request.motivo === 0 ? 0 : request.motivo === 1 ? 2 : 3;
          return {
            ...animal,
            status: status,
            fechaBaja: request.fechaBaja,
            bajaNotas: request.notas
          };
        }
        return animal;
      })
    }));

    showSuccessToast('Animal dado de baja');
  },
  cambiarZona: async (request: CambiarZonaRequest) => {
    const response = await cambiarZona(request);

    if (response.isFailure) return;

    set((state) => ({
      enCorral: state.enCorral.map((animal) => {
        if (animal.folioInterno === request.folioInterno && animal.partida === request.partida) {
          return {
            ...animal,
            idZona: request.idZona,
            zona: response.result
          };
        }
        return animal;
      })
    }));

    showSuccessToast('Zona actualizada correctamente');
  },
  quitarDatosPreñada: async (request: QuitarDatosPreñadaRequest) => {
    const response = await quitarDatosPreñada(request);

    if (response.isFailure) return;

    set((state) => ({
      enCorral: state.enCorral.map((animal) => {
        if (animal.folioInterno === request.folioInterno && animal.partida === request.partida) {
          return {
            ...animal,
            cargada: false,
            fechaTacto: null,
            fechaParto: null,
            mesesGestacion: null,
            cargadaNotas: null,
            parida: false,
            paridaTipo: null,
            paridaNotas: null
          };
        }
        return animal;
      })
    }));

    showSuccessToast('Status actualizado correctamente');
  },
  quitarDatosParida: async (request: QuitarDatosParidaRequest) => {
    const response = await quitarDatosParida(request);

    if (response.isFailure) return;

    set((state) => ({
      enCorral: state.enCorral.map((animal) => {
        if (animal.folioInterno === request.folioInterno && animal.partida === request.partida) {
          return {
            ...animal,
            parida: false,
            paridaTipo: null,
            paridaNotas: null
          };
        }
        return animal;
      })
    }));

    showSuccessToast('Status actualizado correctamente');
  },
  quitarDatosBaja: async (request: QuitarDatosBajaRequest) => {
    const response = await quitarDatosBaja(request);

    if (response.isFailure) return;

    set((state) => ({
      enCorral: state.enCorral.map((animal) => {
        if (animal.folioInterno === request.folioInterno && animal.partida === request.partida) {
          return {
            ...animal,
            status: 1,
            fechaBaja: null,
            bajaNotas: null
          };
        }
        return animal;
      })
    }));

    showSuccessToast('Status actualizado correctamente');
  }
}));
